import { z } from 'zod';
import {
  EventCents,
  EventDay,
  EventFlag,
  EventItemKey,
  EventNoQuery,
  EventOptionalEnum,
  EventRowId,
  EventText,
  EventWhole,
} from './eventPlanningFields';

// An event's money (/api/events/:id/money, /api/events/money/*). Amounts are whole cents; no card,
// banking or SIN fields exist here, and the note guard in the service refuses them inside text.
// The lists mirror models/enums.ts and the event money rules (the tests compare them).

export const EventCostBucket = z.enum([
  'travel',
  'lodging',
  'booked_rooms',
  'food',
  'interpretation',
  'hybrid_av',
  'parking',
  'gift_cards',
  'speaker_honoraria',
  'promotion_tools',
  'registrations',
  'hst_unrecoverable',
  'other_cost',
]);
export type EventCostBucket = z.infer<typeof EventCostBucket>;

export const EventRevenueBucket = z.enum(['grant', 'york_in_kind', 'eventbrite', 'hst_rebate', 'other_revenue']);
export type EventRevenueBucket = z.infer<typeof EventRevenueBucket>;

export const EventBudgetBucket = z.enum([...EventCostBucket.options, ...EventRevenueBucket.options]);
export type EventBudgetBucket = z.infer<typeof EventBudgetBucket>;

export const EventVendorType = z.enum([
  'hotel',
  'venue_catering',
  'pheedloop',
  'interpretation',
  'av',
  'lanyards',
  'nametag_printing',
  'partner_congress',
  'other',
]);
export type EventVendorType = z.infer<typeof EventVendorType>;

export const EventVendorPaymentMethod = z.enum(['invoice', 'pcard', 'deposit', 'wire']);
export type EventVendorPaymentMethod = z.infer<typeof EventVendorPaymentMethod>;

export const EventVendorStep = z.enum([
  'quote',
  'npi_approved',
  'pcard_limit',
  'contract_signed',
  'payment_arranged',
  'beo_final',
  'invoice_paid',
]);
export type EventVendorStep = z.infer<typeof EventVendorStep>;

export const EventHotelChargeKind = z.enum(['no_show', 'cancellation', 'late_release', 'other']);
export type EventHotelChargeKind = z.infer<typeof EventHotelChargeKind>;

export const EventGiftRoute = z.enum(['pcard', 'participant_fee', 'honorarium']);
export type EventGiftRoute = z.infer<typeof EventGiftRoute>;

/** Every gift-card step key across the routes; the service checks the step belongs to the request's route. */
export const EventGiftStep = z.enum([
  'requested',
  'approved',
  'bought',
  'reconciled',
  'logged',
  'given',
  'form_sent',
  'paid',
]);
export type EventGiftStep = z.infer<typeof EventGiftStep>;

// ---------------------------------------------------------------- params and queries

export const EventMoneyParams = z.strictObject({ id: EventRowId });
export const EventMoneyLineParams = z.strictObject({ lineId: EventRowId });
export const EventMoneyVendorParams = z.strictObject({ vendorId: EventRowId });
export const EventMoneyVendorStepParams = z.strictObject({
  vendorId: EventRowId,
  step: z.enum(EventVendorStep.options, { error: 'That step does not apply to this vendor' }),
});
/** `new` adds a deadline. */
export const EventMoneyDeadlineParams = z.strictObject({ vendorId: EventRowId, key: EventItemKey });
export const EventMoneyGiftParams = z.strictObject({ requestId: EventRowId });
export const EventMoneyNoQuery = EventNoQuery;

/** GET /api/events/:id/money/steps?kinds=reimbursement,honorarium (unknown kinds match no entry). */
export const EventMoneyStepsQuery = z.strictObject({ kinds: z.string().max(500).optional() });
export type EventMoneyStepsQuery = z.input<typeof EventMoneyStepsQuery>;

// ---------------------------------------------------------------- requests

const TreasuryTicketType = z.strictObject({
  name: EventText(80),
  priceCents: EventCents,
  capacity: EventWhole,
});

/** PUT /api/events/:id/money/settings; only the fields sent change. */
export const EventMoneySettingsRequest = z.strictObject({
  costCentre: EventText(64),
  eReports: z.partialRecord(EventBudgetBucket, EventCents).optional(),
  eReportsAsOf: EventDay,
  eventbriteRequest: z
    .strictObject({
      salesOpen: EventDay,
      salesClose: EventDay,
      expected: EventWhole,
      feesAbsorbed: z.boolean().nullish(),
      ticketTypes: z.array(TreasuryTicketType).max(50),
    })
    .partial()
    .nullish(),
});
export type EventMoneySettingsRequest = z.input<typeof EventMoneySettingsRequest>;

/** POST /api/events/:id/money/treasury */
export const EventTreasuryMarkRequest = z.strictObject({
  which: z.enum(['request', 'sales_report'], { error: 'Say which: the request or the sales report' }),
  undo: z.boolean().optional(),
});
export type EventTreasuryMarkRequest = z.input<typeof EventTreasuryMarkRequest>;

const lineShape = {
  bucket: z.enum(EventBudgetBucket.options, { error: 'Choose a budget bucket' }),
  label: EventText(200),
  plannedCents: EventCents,
  committedCents: EventCents,
  actualCents: EventCents,
  note: EventText(500),
};

/** POST /api/events/:id/money/import: a budget sheet (.xlsx, base64); a dry run unless confirm is true. */
export const EventBudgetImportRequest = z.strictObject({
  fileName: z.string().max(500).nullish(),
  content: z.string().nullish(),
  sheet: z.string().max(200).nullish(),
  confirm: z.boolean().optional(),
});
export type EventBudgetImportRequest = z.input<typeof EventBudgetImportRequest>;

/** POST /api/events/:id/money/lines (the label is required by the service). */
export const EventBudgetLineRequest = z.strictObject(lineShape);
export type EventBudgetLineRequest = z.input<typeof EventBudgetLineRequest>;

/** PATCH /api/events/money/lines/:lineId */
export const EventBudgetLineUpdateRequest = z.strictObject({ ...lineShape, archived: EventFlag }).partial();
export type EventBudgetLineUpdateRequest = z.input<typeof EventBudgetLineUpdateRequest>;

const vendorShape = {
  vendorType: z.enum(EventVendorType.options, { error: 'Choose what kind of vendor this is' }),
  name: EventText(200),
  bucket: z.enum(EventCostBucket.options, { error: 'Choose a cost bucket' }),
  paymentMethod: EventOptionalEnum(EventVendorPaymentMethod.options, 'Unknown payment method'),
  pcardIncrease: EventFlag,
  costCentre: EventText(64),
  quoteCents: EventCents,
  committedCents: EventCents,
  actualCents: EventCents,
  note: EventText(1000),
  // the dashboard's form also sends its dollar inputs; the service reads only the cents
  quote: z.string().max(40).optional(),
  committed: z.string().max(40).optional(),
  actual: z.string().max(40).optional(),
};

/** POST /api/events/:id/money/vendors (the name is required by the service). */
export const EventVendorRequest = z.strictObject({ ...vendorShape, bucket: vendorShape.bucket.optional() });
export type EventVendorRequest = z.input<typeof EventVendorRequest>;

/** PATCH /api/events/money/vendors/:vendorId */
export const EventVendorUpdateRequest = z.strictObject({ ...vendorShape, cancelled: EventFlag }).partial();
export type EventVendorUpdateRequest = z.input<typeof EventVendorUpdateRequest>;

/** POST /api/events/money/vendors/:vendorId/steps/:step */
export const EventVendorStepRequest = z.strictObject({
  undo: z.boolean().optional(),
  note: EventText(500),
  costCentre: EventText(64),
});
export type EventVendorStepRequest = z.input<typeof EventVendorStepRequest>;

/** POST /api/events/money/vendors/:vendorId/beo */
export const EventVendorBeoRequest = z.strictObject({ label: EventText(60), date: EventDay, note: EventText(500) });
export type EventVendorBeoRequest = z.input<typeof EventVendorBeoRequest>;

/** PATCH /api/events/money/vendors/:vendorId/deadlines/:key */
export const EventVendorDeadlineRequest = z.strictObject({
  dueOn: EventDay,
  done: EventFlag,
  label: EventText(200),
});
export type EventVendorDeadlineRequest = z.input<typeof EventVendorDeadlineRequest>;

/** POST /api/events/money/vendors/:vendorId/charges (hotel no-shows and cancellations). */
export const EventVendorChargeRequest = z.strictObject({
  on: EventDay,
  kind: z.enum(EventHotelChargeKind.options, { error: `Kind is one of ${EventHotelChargeKind.options.join(', ')}` }),
  rooms: EventWhole,
  nights: EventWhole,
  chargeCents: EventCents,
  note: EventText(500),
});
export type EventVendorChargeRequest = z.input<typeof EventVendorChargeRequest>;

const giftShape = {
  route: EventOptionalEnum(EventGiftRoute.options, 'Unknown route'),
  count: EventWhole,
  valueCents: EventCents,
  use: EventText(300),
  vendor: EventText(120),
  recipients: EventText(300),
  note: EventText(1000),
  // the dashboard's form also sends its dollar input; the service reads valueCents
  value: z.string().max(40).optional(),
};

/** POST /api/events/:id/money/gift-cards */
export const EventGiftRequestRequest = z.strictObject(giftShape);
export type EventGiftRequestRequest = z.input<typeof EventGiftRequestRequest>;

/** PATCH /api/events/money/gift-cards/:requestId; `step` marks (or with `undo` clears) one step. */
export const EventGiftRequestUpdateRequest = z
  .strictObject({
    ...giftShape,
    cancelled: EventFlag,
    step: z.union([z.enum(EventGiftStep.options, { error: 'That step does not apply' }), z.literal(''), z.null()]),
    undo: EventFlag,
  })
  .partial();
export type EventGiftRequestUpdateRequest = z.input<typeof EventGiftRequestUpdateRequest>;

/** POST /api/events/:id/money/default-routes (kinds without a default route are skipped). */
export const EventDefaultRoutesRequest = z.strictObject({
  kinds: z.union([z.array(z.string().max(40)).max(20), z.string().max(40)]).optional(),
});
export type EventDefaultRoutesRequest = z.input<typeof EventDefaultRoutesRequest>;
